import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import IconButton from 'LuxKit/Button/IconButton'
import LineMultiplyIcon from 'LuxKit/Icons/line/LineMultiplyIcon'
import { matchPath, useRouteMatch } from 'react-router'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffer from 'hooks/Offers/useOffer'
import { useCurrentSiteTakeoverV2 } from '../hooks/useCurrentSiteTakeover'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'

const PopupContainer = styled.div`
  position: fixed;
  display: flex;
  padding: ${rem(8)} ${rem(12)};
  align-items: center;
  overflow: hidden;
  border-radius: ${rem(8)};
  bottom: ${rem(10)};
  right: ${rem(12)};
  left: ${rem(12)};
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: white;
  
  ${mediaQueryUp.desktop} {
    width: ${rem(343)};
    padding: ${rem(20)};
    left: unset;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
`

const PopupBackground = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
  z-index: 0;
`

const PopupContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
`

const MobilePopupContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const MobileDismissButton = styled.div`
  padding: ${rem(4)};
  border-radius: ${rem(2)};
  cursor: pointer;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const MobileTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const MobileSubtitleRow = styled.div`
  display: flex;
  gap: ${rem(8)};
`

const TextContentWrapper = styled.div`
  flex: 1;
`

const DesktopPopupContent = styled(PopupContent)`
  display: none;
  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
  }
`

const PopupTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(8)};
  align-self: stretch;
`

const DesktopTextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const SubheadingWrapper = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: ${rem(2)} ${rem(8)};
  align-self: stretch;
  flex-wrap: wrap;
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
  '/:regionCode/tour/:slug/:offerId',
  '/:regionCode/partner/:slug/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
  '/:regionCode/account',
  '/:regionCode/booking',
  '/:regionCode/bb/booking',
  '/:regionCode/trip-planner/generate-trip',
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const invalidExactRoutes: Array<string> = [
  '/:regionCode',
  '/:regionCode/triple-points',
]

const homePageRoutes: Array<string> = [
  '/:regionCode',
]

function SiteTakeoverPopup() {
  const match = useRouteMatch<{ offerId: string }>(offerPageRoutes)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  // Call all hooks unconditionally
  const siteTakeoverV2 = useCurrentSiteTakeoverV2()
  const siteTakeover = siteTakeoverV2

  const siteTakeoverOnTourV2 = useCurrentSiteTakeoverV2({ heroVertical: 'tours' })
  const siteTakeoverOnTour = siteTakeoverOnTourV2

  const showingOfferBanner = !!(offer && siteTakeover?.offerTypes?.has(offer.type))
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const [isDismissed, setIsDismissed] = useState(false)
  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const popupPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const hide = useMemo((): boolean => {
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner.exceptedRoutes ?? EmptyArray
    // hide on:
    // - The home page (has its own banner)
    // - Search page results (has it's own banner)
    // - Would show the offer page banner (has it's own banner)
    // - Any of the other no banner routes (shouldn't show banners)
    // - When tour has it's own takeover
    return showingOfferBanner ||
      !!matchPath(currentPath, { path: homePageRoutes, exact: true }) ||
      !!matchPath(currentPath, { path: invalidExactRoutes, exact: true }) ||
      (!!exceptedRoutes.length && !!matchPath(currentPath, { path: exceptedRoutes })) ||
      !!matchPath(currentPath, { path: noBannerRoutes }) ||
      (!!siteTakeoverOnTour && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, showingOfferBanner, siteTakeover, siteTakeoverOnTour])

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
  }, [])

  if (!siteTakeoverTheme || !popupPayload || isDismissed || hide) {
    return null
  }

  const logoRef = siteTakeoverTheme.logoRef
  const backgroundStyle = popupPayload.genericBannerType === 'whitepopup' ? 'white' : siteTakeoverTheme.backgroundStyle
  const title = popupPayload.title
  const subtitle = popupPayload.subtitle
  const ctaLink = popupPayload.ctaLink

  const isDismissible = popupPayload.isDismissible
  const paletteType = popupPayload.genericBannerType === 'whitepopup' ? 'default' : (siteTakeoverTheme.palette ?? 'inverse')

  return (
    <PopupContainer className={getThemeClassName({ mobile: paletteType })}>
      <PopupBackground backgroundStyle={backgroundStyle} />

      <MobilePopupContent>
        {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
        <TextContentWrapper>
          <MobileTitleRow>
            <BodyText weight="bold" variant="medium">{title}</BodyText>
          </MobileTitleRow>
          <MobileSubtitleRow>
            <BodyText variant="small">{subtitle}</BodyText>
            {ctaLink && (
              <TextLink to={ctaLink} weight="bold" size="small">View all</TextLink>
            )}
          </MobileSubtitleRow>
        </TextContentWrapper>
        {isDismissible && (
          <MobileDismissButton onClick={onDismissHandler}>
            <LineMultiplyIcon size="S" />
          </MobileDismissButton>
        )}
      </MobilePopupContent>

      <DesktopPopupContent>
        <PopupTitleRow>
          {logoRef && <Image image={{ id: logoRef }} dpr={2} width={48} height={48} />}
          <DesktopTextContent>
            <BodyText weight="bold" variant="large">{title}</BodyText>
          </DesktopTextContent>
          {isDismissible && (
            <IconButton
              kind="tertiary"
              variant="dark"
              onClick={onDismissHandler}
            >
              <LineMultiplyIcon size="S" />
            </IconButton>
          )}
        </PopupTitleRow>
        <SubheadingWrapper>
          <BodyText variant="medium">{subtitle}</BodyText>
          {ctaLink && (
            <TextLink to={ctaLink} weight="bold" size="medium">View all offers</TextLink>
          )}
        </SubheadingWrapper>
      </DesktopPopupContent>
    </PopupContainer>
  )
}

export default SiteTakeoverPopup
